import React from 'react';

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

const VARIANT_STYLES: Record<BadgeVariant, string> = {
  success: 'bg-green-50 text-green-700 border-green-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  error: 'bg-red-50 text-red-700 border-red-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  neutral: 'bg-gray-50 text-gray-600 border-gray-200',
  default: 'bg-slate-50 text-slate-600 border-slate-200',
};

const DOT_STYLES: Record<BadgeVariant, string> = {
  success: 'bg-green-500',
  warning: 'bg-amber-500',
  error: 'bg-red-500',
  info: 'bg-blue-500',
  neutral: 'bg-gray-400',
  default: 'bg-slate-400',
};

function variantForStatus(status: string): BadgeVariant {
  const s = status.toLowerCase();
  if (['active', 'verified', 'completed', 'confirmed', 'accepted', 'paid', 'delivered', 'buyer_accepted',
       'quality_accepted', 'resolved', 'passed'].includes(s)) return 'success';
  if (['draft', 'pending', 'awaiting_confirmation', 'submitted', 'invited', 'assigned', 'processing',
       'under_review', 'open', 'partially_fulfilled', 'partially_delivered', 'in_progress',
       'quotation_open', 'evaluation', 'revised'].includes(s)) return 'warning';
  if (['cancelled', 'rejected', 'failed', 'expired', 'suspended', 'deleted', 'exception',
       'disputed', 'quality_rejected', 'closed'].includes(s)) return 'error';
  if (['published', 'in_transit', 'stock_reserved', 'preparing', 'ready_for_dispatch',
       'ready_for_quality_check', 'correction_required', 'partially_awarded', 'awarded',
       'partially_awarded', 'escalated'].includes(s)) return 'info';
  if (['paused', 'sold_out', 'viewed', 'overdue', 'withdrawn'].includes(s)) return 'neutral';
  return 'default';
}

function formatStatusLabel(status: string): string {
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

interface StatusBadgeProps {
  status: string;
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  showDot?: boolean;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, variant, size = 'md', showDot = true }) => {
  const v = variant || variantForStatus(status);
  const sizeClass = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-xs';

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border font-semibold ${VARIANT_STYLES[v]} ${sizeClass}`}>
      {showDot && <span className={`w-1.5 h-1.5 rounded-full ${DOT_STYLES[v]}`} />}
      {formatStatusLabel(status)}
    </span>
  );
};

export default StatusBadge;
export { variantForStatus, formatStatusLabel };
